import React, { useState } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import ResourceSelector from './ResourceSelector';

/**
 * ResourcePageGroup - displays a page with its images
 * Supports expand/collapse and page-level selection
 */
function ResourcePageGroup({
    page,
    selectedPages,
    selectedImages,
    onPageSelect,
    onImageSelect,
    onImageHover,
    onImageFocus,
    webpSizes,
    canSelectPage = true,
}) {
    const [isExpanded, setIsExpanded] = useState(true);
    
    const isPageSelected = selectedPages.has(page.pageId);
    const allImagesSelected = page.imageIds.length > 0 && 
        page.imageIds.every(imgId => selectedImages.has(imgId));
    
    const handlePageCheckboxChange = (e) => {
        e.stopPropagation();
        onPageSelect(page.pageId, !isPageSelected);
    };
    
    const handleToggleExpand = () => {
        setIsExpanded(!isExpanded);
    };
    
    return (
        <div className="border border-gray-300 rounded-lg overflow-hidden">
            {/* Page header */}
            <div
                className={cx(
                    'flex items-center gap-3 p-4 cursor-pointer transition-colors',
                    'hover:bg-gray-50',
                    isPageSelected && 'bg-blue-50'
                )}
                onClick={handleToggleExpand}
                role="button"
                tabIndex={-1}
                aria-expanded={isExpanded}
            >
                {canSelectPage && (
                    <input
                        type="checkbox"
                        checked={isPageSelected || allImagesSelected}
                        onChange={handlePageCheckboxChange}
                        onClick={(e) => e.stopPropagation()}
                        tabIndex={-1}
                        className="w-4 h-4 cursor-pointer"
                    />
                )}
                <button
                    className="flex-1 text-left font-semibold flex items-center gap-2 hover:opacity-80 transition-opacity"
                    onClick={handleToggleExpand}
                    tabIndex={-1}
                >
                    <span className={cx(
                        'transition-transform text-xs',
                        isExpanded && 'rotate-90'
                    )}>
                        ▶
                    </span>
                    <span>{page.pageName}</span>
                    <span className="text-sm font-normal opacity-60">
                        ({page.images.length} image{page.images.length !== 1 ? 's' : ''})
                    </span>
                </button>
            </div>
            
            {/* Images list */}
            {isExpanded && (
                <div className="border-t border-gray-200 bg-gray-50">
                    <div className="p-4 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                        {page.images.map((file, index) => {
                            const imageId = page.imageIds[index];
                            // Ensure imageId is a string (handle edge cases)
                            let imageIdString;
                            if (typeof imageId === 'string' && imageId) {
                                imageIdString = imageId;
                            } else if (imageId != null) {
                                imageIdString = String(imageId);
                            } else {
                                // Fallback: use pageId + index for unique key
                                imageIdString = `${page.pageId}-${index}`;
                            }
                            
                            return (
                                <ResourceSelector
                                    key={imageIdString}
                                    imageId={imageIdString}
                                    file={file}
                                    isSelected={selectedImages.has(imageIdString)}
                                    onSelect={onImageSelect}
                                    onHover={onImageHover}
                                    onFocus={onImageFocus}
                                    webpSizeInfo={webpSizes[imageIdString]}
                                />
                            );
                        })}
                    </div>
                </div>
            )}
        </div>
    );
}

ResourcePageGroup.propTypes = {
    page: PropTypes.shape({
        pageId: PropTypes.string.isRequired,
        pageName: PropTypes.string.isRequired,
        pagePath: PropTypes.string.isRequired,
        images: PropTypes.array.isRequired,
        imageIds: PropTypes.array.isRequired,
    }).isRequired,
    selectedPages: PropTypes.instanceOf(Set).isRequired,
    selectedImages: PropTypes.instanceOf(Set).isRequired,
    onPageSelect: PropTypes.func.isRequired,
    onImageSelect: PropTypes.func.isRequired,
    onImageHover: PropTypes.func,
    onImageFocus: PropTypes.func,
    webpSizes: PropTypes.object,
    canSelectPage: PropTypes.bool,
};

export default ResourcePageGroup;
